/**
 * strengthStandards — cold-start 1RM seeding.
 *
 * WHY THIS EXISTS
 * ---------------
 * Load prescription needs a 1RM. `getIntelligentStartingWeight` derives most
 * accessory loads from a related big-lift 1RM (`relatedRM` x `rmFraction`), and
 * `detectTrainingLevel` only scores strength when it has BOTH 1RMs and a
 * bodyweight. Before this file existed, onboarding never wrote a single 1RM, so
 * on day one every user hit the same dead end:
 *
 *   0 sessions -> operatorScore ~0
 *   0 oneRMs   -> strength scoring skipped entirely
 *              -> trainingLevel falls through to 'beginner'
 *              -> first workout prescribes the beginner floor (45 lb barbell,
 *                 15 lb accessories) to EVERYONE, regardless of what they said.
 *
 * Self-reported `experienceLevel` never reached the load math at all — it is
 * only read for ACWR thresholds. So an advanced lifter's first session looked
 * identical to an untrained one's, and they left. This seeds the four keys the
 * rest of the engine actually looks up (see RELATED_RM_ALIASES and the
 * detectTrainingLevel call site in smartWorkoutGenerator).
 *
 * THE NUMBERS
 * -----------
 * Population strength standards expressed as a multiple of bodyweight, by sex
 * and training level (the widely-replicated ExRx.net / Kilgore-style norms).
 * Each value is the ENTRY point of its band — the number that qualifies you for
 * that level, not the middle or top of it. That makes a seed structurally
 * conservative without applying an invented fudge factor. (Fabricated constants
 * dressed up as model output are precisely what the ML audit found and removed;
 * we are not reintroducing one here.)
 *
 * A seed is a PRIOR, not a measurement. It is written with source 'calculated'
 * (confidence 0.8) so any tested or manually-entered 1RM outranks it, and real
 * logged sets revise it from the very first session. Under-seeding costs one
 * easy session that the RPE gate corrects upward; over-seeding is an injury
 * risk. When in doubt this table errs low — including for unspecified sex,
 * which uses the lower of the two tables.
 */

import { calculate1RMEpley } from './oneRM';

export type StrengthSex = 'male' | 'female';
export type StrengthLevel = 'beginner' | 'intermediate' | 'advanced' | 'elite';

/** The exact 1RM keys the generator looks up. Do not rename without updating
 *  RELATED_RM_ALIASES and the detectTrainingLevel call site. */
export const SEED_LIFTS = ['Back Squat', 'Bench Press', 'Deadlift', 'Overhead Press'] as const;
export type SeedLift = (typeof SEED_LIFTS)[number];

/** 1RM as a multiple of bodyweight. Entry value of each band. */
const BW_MULTIPLES: Record<StrengthSex, Record<StrengthLevel, Record<SeedLift, number>>> = {
  male: {
    beginner:     { 'Back Squat': 1.00, 'Bench Press': 0.75, Deadlift: 1.25, 'Overhead Press': 0.45 },
    intermediate: { 'Back Squat': 1.45, 'Bench Press': 1.10, Deadlift: 1.75, 'Overhead Press': 0.65 },
    advanced:     { 'Back Squat': 1.90, 'Bench Press': 1.45, Deadlift: 2.30, 'Overhead Press': 0.85 },
    elite:        { 'Back Squat': 2.30, 'Bench Press': 1.75, Deadlift: 2.75, 'Overhead Press': 1.00 },
  },
  female: {
    beginner:     { 'Back Squat': 0.75, 'Bench Press': 0.45, Deadlift: 1.00, 'Overhead Press': 0.30 },
    intermediate: { 'Back Squat': 1.15, 'Bench Press': 0.65, Deadlift: 1.40, 'Overhead Press': 0.42 },
    advanced:     { 'Back Squat': 1.55, 'Bench Press': 0.90, Deadlift: 1.85, 'Overhead Press': 0.55 },
    elite:        { 'Back Squat': 1.90, 'Bench Press': 1.10, Deadlift: 2.25, 'Overhead Press': 0.68 },
  },
};

const roundTo5 = (n: number) => Math.max(5, Math.round(n / 5) * 5);

/**
 * Relative-strength band thresholds for detectTrainingLevel, PER SEX.
 *
 * The classifier scores strength as an average bodyweight-multiple of
 * squat/bench/deadlift, then buckets it. It used to bucket with a single
 * male-normed set of cutoffs (elite ≥1.75, advanced ≥1.5, intermediate ≥1.0)
 * applied to everyone. Women carry systematically lower bodyweight-strength
 * ratios — most of the gap is upper body, i.e. the bench the average weights
 * equally with the deadlift — so a female "advanced" lifter (avg ~1.43×BW)
 * scored INTERMEDIATE, and male seeds (avg ~1.88×BW) scored ELITE. The bands
 * were wrong in both directions because they were one sex's numbers.
 *
 * These thresholds are DERIVED from the same BW_MULTIPLES the seeds use: the
 * cutoff to qualify as level L is the average squat/bench/deadlift multiple at
 * the ENTRY of level L, for that sex. That makes the seed and the classifier
 * self-consistent by construction — a freshly-seeded "advanced" woman scores
 * "advanced", not one bucket down — and keeps a single published source of
 * truth instead of a second hand-tuned (and quietly male-only) table.
 */
const STRENGTH_BAND_LIFTS: SeedLift[] = ['Back Squat', 'Bench Press', 'Deadlift'];

export function strengthBandsFor(sex: StrengthSex | undefined):
  { intermediate: number; advanced: number; elite: number } {
  const table = BW_MULTIPLES[sex ?? 'female'];
  const avgEntry = (level: StrengthLevel) =>
    STRENGTH_BAND_LIFTS.reduce((s, l) => s + table[level][l], 0) / STRENGTH_BAND_LIFTS.length;
  return {
    intermediate: avgEntry('intermediate'),
    advanced: avgEntry('advanced'),
    elite: avgEntry('elite'),
  };
}

/**
 * Estimate the four seed 1RMs from bodyweight, sex and self-reported level.
 * Unspecified sex falls back to the female (lower) table — see file header.
 */
export function estimateSeedOneRMs(
  bodyWeightLbs: number,
  sex: StrengthSex | undefined,
  level: StrengthLevel,
): Record<SeedLift, number> {
  const table = BW_MULTIPLES[sex ?? 'female'][level];
  const out = {} as Record<SeedLift, number>;
  for (const lift of SEED_LIFTS) {
    out[lift] = roundTo5(bodyWeightLbs * table[lift]);
  }
  return out;
}

/** Reps beyond this leave the validated range of the Epley/Brzycki equations,
 *  where the error grows fast and asymmetrically. Mirrors MAX_E1RM_REPS in the
 *  dynamic-1RM path — a 20-rep burnout set must never set someone's anchor. */
export const MAX_SEED_REPS = 12;

/**
 * Convert a set the user actually remembers ("225 for 5") into a 1RM.
 *
 * Most people do not know a true tested 1RM but do know a recent working set,
 * and a rep-max they have actually performed is a far better prior than a
 * population multiple. Returns 0 when the input is outside the equation's
 * validated range, so callers fall back to the table rather than trusting a
 * number the math cannot support.
 */
export function seedOneRMFromRepMax(weightLbs: number, reps: number): number {
  if (!weightLbs || weightLbs <= 0) return 0;
  if (!Number.isFinite(reps) || reps < 1 || reps > MAX_SEED_REPS) return 0;
  if (reps === 1) return roundTo5(weightLbs);
  return roundTo5(calculate1RMEpley(weightLbs, reps));
}
